// /reports/cashiers — Cashier Performance (PAGES.md §3.46)

import { useState, useMemo } from 'react';
import type { ColumnDef, TimeRangeValue } from '@bbpos/ui';
import { Avatar, Badge } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { getSubunitMultiplier } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';
import {
  ReportPageShell,
  ReportFilterBar,
  ReportTable,
  ReportExportMenu,
  ReportSkeleton,
  ReportError,
  useReportFilter,
  useReportQuery,
  reportsApi,
  downloadCsv,
  shareReportLink,
  DEFAULT_REPORT_PRESET,
} from '@/features/reports';
import type { CashierPerformanceRow } from '@/features/reports';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';

type Row = CashierPerformanceRow & { id: string };

function makeColumns(currency: CurrencyCode): ColumnDef<Row>[] {
  return [
    {
      key: 'name',
      header: 'Cashier',
      render: (_v, row) => (
        <div className="flex items-center gap-3">
          <Avatar name={row.name} size="sm" />
          <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{row.name}</span>
        </div>
      ),
    },
    { key: 'total_sales', header: 'Total Sales', numeric: true, render: (_v, row) => <span className="text-sm font-semibold tabular-nums">{formatPaise(row.total_sales, currency)}</span> },
    { key: 'bill_count', header: 'Bills', numeric: true, render: (_v, row) => <span className="tabular-nums">{row.bill_count.toLocaleString('en-IN')}</span> },
    { key: 'avg_bill_value', header: 'Avg Bill', numeric: true, render: (_v, row) => <span className="tabular-nums">{formatPaise(row.avg_bill_value, currency)}</span> },
    { key: 'return_count', header: 'Returns', numeric: true, render: (_v, row) => <Badge variant={row.return_count > 0 ? 'warning' : 'success'} size="sm">{row.return_count}</Badge> },
    { key: 'void_count', header: 'Voids', numeric: true, render: (_v, row) => <Badge variant={row.void_count > 0 ? 'danger' : 'success'} size="sm">{row.void_count}</Badge> },
    { key: 'hours_worked', header: 'Hours', numeric: true, render: (_v, row) => <span className="tabular-nums">{row.hours_worked.toFixed(1)}</span> },
  ];
}

export function CashierPerformancePage() {
  const [timeRange, setTimeRange] = useState<TimeRangeValue>(DEFAULT_REPORT_PRESET);
  const [locationId, setLocationId] = useState<string | undefined>(undefined);
  const filter = useReportFilter(timeRange, locationId);
  const currency = useTenantCurrency();
  const symbol = getCurrencySymbol(currency);
  const subunit = getSubunitMultiplier(currency);
  const csvDecimals = Math.round(Math.log10(subunit));
  const columns = useMemo(() => makeColumns(currency), [currency]);

  const { data, isLoading, error } = useReportQuery({
    key: 'cashiers',
    filter,
    queryFn: (f) => reportsApi.getCashierPerformance(f),
  });

  const rows: Row[] = useMemo(
    () => (data?.rows ?? []).map((r) => ({ ...r, id: r.user_id })),
    [data],
  );

  const handleExport = (fmt: 'csv' | 'pdf' | 'whatsapp' | 'email') => {
    if (fmt === 'pdf') {
      // Browser print dialog -> "Save as PDF"; no server-side PDF renderer exists.
      window.print();
      return;
    }
    if (fmt === 'csv') {
      downloadCsv(
        `cashier-performance-${filter.range.from}-to-${filter.range.to}.csv`,
        [
          { header: 'Cashier', get: (r: Row) => r.name },
          { header: `Total Sales (${symbol})`, get: (r: Row) => (r.total_sales / subunit).toFixed(csvDecimals) },
          { header: 'Bills', get: (r: Row) => r.bill_count },
          { header: `Avg Bill (${symbol})`, get: (r: Row) => (r.avg_bill_value / subunit).toFixed(csvDecimals) },
          { header: 'Returns', get: (r: Row) => r.return_count },
          { header: 'Voids', get: (r: Row) => r.void_count },
          { header: 'Hours', get: (r: Row) => r.hours_worked },
        ],
        rows,
      );
      return;
    }
    if (fmt === 'whatsapp' || fmt === 'email') {
      shareReportLink(fmt, 'Orio360 Cashier Performance', `${rows.length} cashiers • Top: ${rows[0]?.name ?? '—'}`);
    }
  };

  return (
    <ReportPageShell
      title="Cashier Performance"
      subtitle="Sales, bills, returns and shift hours per cashier."
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Reports', href: '/reports' }, { label: 'Cashier Performance' }]}
      accent="brand-indigo"
      headerActions={<ReportExportMenu onExport={handleExport} disabled={!rows.length} />}
      filterBar={<ReportFilterBar timeRange={timeRange} onTimeRangeChange={setTimeRange} locationId={locationId} onLocationChange={setLocationId} />}
    >
      {isLoading ? (
        <ReportSkeleton kpiCount={0} />
      ) : error ? (
        <ReportError />
      ) : (
        <ReportTable title="Performance by Cashier" columns={columns} data={rows} emptyTitle="No cashier data for selected period" totalCount={rows.length} />
      )}
    </ReportPageShell>
  );
}
